import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";

export type BannerTone = "info" | "success" | "warning" | "danger" | "volt";

export interface BannerProps {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Optional CTA / action slot on the right. */
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

const tones: Record<
  BannerTone,
  { wrap: string; icon: string; glyph: ReactNode }
> = {
  info: {
    wrap: "bg-ocean-soft ring-ocean/15 text-body",
    icon: "text-ocean",
    glyph: <path d="M12 8h.01M11 12h1v4h1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />,
  },
  success: {
    wrap: "bg-lime-soft ring-lime/15 text-body",
    icon: "text-lime",
    glyph: <path d="m8 12 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />,
  },
  warning: {
    wrap: "bg-amber-soft ring-amber/20 text-body",
    icon: "text-amber",
    glyph: <path d="M12 8v5m0 3h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />,
  },
  danger: {
    wrap: "bg-coral-soft ring-coral/15 text-body",
    icon: "text-coral",
    glyph: <path d="M12 8v5m0 3h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />,
  },
  // Celebratory / signal banner — full coral→orange gradient.
  volt: {
    wrap: "bg-grad-coral ring-transparent text-white shadow-volt",
    icon: "text-white",
    glyph: <path d="m13 3-8 10h6l-1 8 8-10h-6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="currentColor" fillOpacity="0.3" />,
  },
};

/**
 * Inline alert / announcement bar. role="status" so assistive tech announces
 * it without stealing focus; dismiss button is fully labelled.
 */
export function Banner({
  tone = "info",
  title,
  children,
  action,
  onDismiss,
  className,
}: BannerProps) {
  const { t } = useTranslation();
  const toneStyle = tones[tone];
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-3 rounded-lg p-4 ring-1 ring-inset",
        toneStyle.wrap,
        className,
      )}
    >
      <svg viewBox="0 0 24 24" className={cn("mt-0.5 h-5 w-5 shrink-0", toneStyle.icon)} fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.35" />
        {toneStyle.glyph}
      </svg>
      <div className="min-w-0 flex-1">
        {title && <p className="font-display text-sm font-bold">{title}</p>}
        {children && <div className={cn("text-sm", title && "mt-0.5", "opacity-90")}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("banner.dismiss")}
          className="-mr-1 -mt-1 grid h-7 w-7 shrink-0 place-items-center rounded-sm opacity-60 hover:opacity-100"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
